import React, { useCallback } from 'react';
import Transition from 'react-transition-group/Transition';
import { useDispatch, useSelector } from 'react-redux';
import { HeaderText, Text2 } from '@el8/vital';
import { State } from 'types';
import Calendar from 'views/booking/Calendar/Calendar';
import { getAppointmentType } from 'modules/appointmentType';
import {
  getBookingInfo,
  selectAvailability as selectAvailabilityAction,
  updateBookingInfo,
} from 'modules/booking';
import { usePractice } from 'modules/practice';
import { DateTime } from 'luxon';
import NextAvailabilities from './NextAvailabilities';

import styles from './Service.less';

interface Props {
  appointmentTypeId: number;
  monthAvailabilities: { [key: string]: string[] };
  navigate: (to: string) => void;
  nextAvailabilities: { [key: string]: string[] };
  physicianId?: number;
  practiceId: number;
  serviceLocationId?: number;
}

const Service2 = ({
  appointmentTypeId,
  monthAvailabilities,
  navigate,
  nextAvailabilities,
  physicianId,
  practiceId,
  serviceLocationId,
}: Props): JSX.Element => {
  const appointmentType = useSelector((state: State) =>
    getAppointmentType(state, { appointmentTypeId }),
  );
  const { name, description } = appointmentType;

  const { calendar } = useSelector(getBookingInfo);

  const dispatch = useDispatch();
  const [{ timezone: practiceTimezone }] = usePractice(practiceId);
  const selectAvailability = useCallback(
    (date, time, selectedTimezone: string) => {
      const dateTimeString = `${date}T${time}`; // Create datetime string in ISO format

      // Create a DateTime object in selected timezone
      const dateTimeInSelectedTimezone = DateTime.fromISO(dateTimeString, {
        zone: selectedTimezone,
      });

      // Convert the datetime to practice timezone
      const dateTimeInPracticeTimezone = dateTimeInSelectedTimezone.setZone(practiceTimezone);

      // Format the date and time separately
      const dateInPracticeTimezone = dateTimeInPracticeTimezone.toFormat('yyyy-MM-dd');
      const timeInPracticeTimezone = dateTimeInPracticeTimezone.toFormat('HH:mm');

      dispatch(
        selectAvailabilityAction(
          dateInPracticeTimezone,
          timeInPracticeTimezone,
          appointmentTypeId,
          serviceLocationId,
          physicianId,
        ),
      );
      navigate(`/book/${practiceId}/account`);
    },
    [
      appointmentTypeId,
      dispatch,
      navigate,
      physicianId,
      practiceId,
      practiceTimezone,
      serviceLocationId,
    ],
  );

  const showMoreAvailabilities = useCallback(
    (currentMonth) => {
      dispatch(
        updateBookingInfo({
          calendar: {
            activeAppointmentTypeId: appointmentTypeId,
            activePhysicianId: physicianId,
            activeServiceLocationId: serviceLocationId,
            currentMonth,
            isLoadingAvailabilities: false,
          },
        }),
      );
    },
    [appointmentTypeId, dispatch, physicianId, serviceLocationId],
  );

  if (window.el8Globals.FEATURES.AvailabilityOnBkSite && !nextAvailabilities) {
    return null;
  }

  const shouldShowCalendar =
    calendar.activeAppointmentTypeId === appointmentTypeId &&
    calendar.activePhysicianId === physicianId &&
    calendar.activeServiceLocationId === serviceLocationId;
  const shouldShowNextAvailabilities = !shouldShowCalendar;

  return (
    <div className={styles.service}>
      <HeaderText className={styles.serviceHeader} legacyVariant="legacyHeaderText" tag="h1">
        {name}
      </HeaderText>
      {description && <Text2 className={styles.info}>{description}</Text2>}
      {nextAvailabilities ? (
        <div className={styles.availabilities}>
          <Transition in={shouldShowCalendar} mountOnEnter unmountOnExit timeout={300}>
            {(state: string): JSX.Element => (
              <Calendar
                locationDates={monthAvailabilities}
                appointmentTypeId={appointmentTypeId}
                practiceId={practiceId}
                selectAvailability={selectAvailability}
                state={state}
              />
            )}
          </Transition>
          <Transition in={shouldShowNextAvailabilities} timeout={300} mountOnEnter unmountOnExit>
            {(state: string): JSX.Element => (
              <NextAvailabilities
                availabilities={nextAvailabilities}
                selectAvailability={selectAvailability}
                showMoreAvailabilities={showMoreAvailabilities}
                state={state}
              />
            )}
          </Transition>
        </div>
      ) : (
        <Text2 className={styles.info}>
          This service cannot be booked online at this time. Please call the office to schedule the
          appointment.
        </Text2>
      )}
    </div>
  );
};

export default Service2;
